import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from '@/i18n'
import { useUseCaseData } from '../../hooks/useUseCaseData'
import UseCaseTable from './UseCaseTable'
import { Checkbox, Button } from 'antd'
import SearchFilter from '@/components/search-filter'
import { Configure } from './configure'
import AddTemplateModal from './add-template-modal'
import DetailDrawer from './detail-drawer'

interface UseCaseSelectorProps {
  taskParams?: any
  onUseCaseSelectionChange?: (
    selectedUseCases: string[],
    useCaseTemplateMap: Record<string, string[]>,
    isSelectAll?: boolean,
  ) => void
  isCreateTaskDisabled?: boolean
}

const UseCaseSelector: React.FC<UseCaseSelectorProps> = ({
  taskParams,
  onUseCaseSelectionChange,
  isCreateTaskDisabled = false,
}) => {
  const [configureVisible, setConfigureVisible] = useState(false)
  const [saveTemplateVisible, setSaveTemplateVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState<{
    visible: boolean
    resourceId: string
  }>({
    visible: false,
    resourceId: '',
  })

  const {
    useCases,
    loading,
    pagination,
    selectedUseCases,
    caseIdList,
    searchUseCases,
    selectUseCase,
    selectAllUseCases,
    handleTableChange,
    handleTableSelectChange,
    attackTypes,
    caseTagList,
    globalUseCaseTemplateMap,
  } = useUseCaseData(taskParams, onUseCaseSelectionChange)

  const tagCascaderOptions = useMemo(
    () =>
      caseTagList.map(item => ({
        value: item.tagCategoryId,
        label: (
          <span>
            <span>{item.tagCategoryName}</span>
            <span style={{ color: 'rgb(32, 144, 168)' }}>({item.tagCategoryNum})</span>
          </span>
        ),
        children: item.tagList.map(tag => ({
          value: tag.tagId,
          label: (
            <span>
              <span>{tag.tagName}</span>
              <span style={{ color: 'rgb(32, 144, 168)' }}>({tag.tagNum})</span>
            </span>
          ),
        })),
      })),
    [caseTagList],
  )

  // 处理用例选择变化
  const handleSelectionChange = (useCaseId: string, selected: boolean) => {
    // 先更新选择状态
    const updatedSelectedUseCases = selected
      ? [...selectedUseCases, useCaseId]
      : selectedUseCases.filter(id => id !== useCaseId)

    selectUseCase(useCaseId, selected)

    // 构建用例模板映射，优先使用全局映射，如果不存在则从当前页数据中获取
    const useCaseTemplateMap: Record<string, string[]> = {}
    updatedSelectedUseCases.forEach(id => {
      // 优先使用全局映射
      if (globalUseCaseTemplateMap[id]) {
        useCaseTemplateMap[id] = globalUseCaseTemplateMap[id]
      } else {
        // 如果全局映射中没有，从当前页数据中获取
        const useCase = useCases.find(u => u.resourceId === id)
        if (useCase) {
          useCaseTemplateMap[id] = useCase.templateIds || []
        }
      }
    })

    // 调用回调函数
    if (onUseCaseSelectionChange) {
      onUseCaseSelectionChange(updatedSelectedUseCases, useCaseTemplateMap, false)
    }
  }

  const handleDetailClick = (resourceId: string) => {
    setDetailVisible({ visible: true, resourceId })
  }

  return (
    <Container>
      <Header>
        <div className="selected-info">
          {t('已选用例')} ({selectedUseCases.length})
        </div>
        <div className="filters">
          <SearchFilter
            defaultSearchType="name"
            searchItems={[
              { key: 'name', label: t('用例名称'), componentType: 'input' },
              {
                key: 'tagId',
                label: t('用例标签'),
                componentType: 'cascader',
                options: tagCascaderOptions,
                style: {
                  minWidth: '120px',
                  maxWidth: '150px',
                },
                cascaderConfig: {
                  expandAtLevel: 1,
                  maxLevel: 2,
                },
              },
            ]}
            onSearch={searchUseCases}
            style={{ width: '100%' }}
          />
          <Button type="primary" onClick={() => setConfigureVisible(true)}>
            {t('全局参数配置')}
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: 6 }}
            disabled={isCreateTaskDisabled}
            onClick={() => {
              if (isCreateTaskDisabled) return
              setSaveTemplateVisible(true)
            }}
          >
            {t('保存为模板')}
          </Button>
        </div>
      </Header>

      <Checkbox
        style={{ marginLeft: 6, marginBottom: 8 }}
        disabled={caseIdList?.length === 0}
        checked={
          caseIdList.length > 0 &&
          selectedUseCases.length === caseIdList.length &&
          caseIdList.every(id => selectedUseCases.includes(id))
        }
        onChange={e => selectAllUseCases(e.target.checked)}
      >
        {t('全选所有')}
      </Checkbox>

      <UseCaseTable
        data={useCases}
        loading={loading}
        pagination={pagination}
        selectedKeys={selectedUseCases}
        attackTypes={attackTypes}
        onSelectionChange={handleSelectionChange}
        onTableChange={handleTableChange}
        onTableSelectChange={handleTableSelectChange}
        onDetailClick={handleDetailClick}
      />
      <Configure configureVisible={configureVisible} setConfigureVisible={setConfigureVisible} />
      <AddTemplateModal
        visible={saveTemplateVisible}
        onClose={() => setSaveTemplateVisible(false)}
        resourceIds={selectedUseCases}
      />
      <DetailDrawer
        visible={detailVisible.visible}
        onCancel={() => setDetailVisible({ visible: false, resourceId: '' })}
        id={detailVisible.resourceId}
      />
    </Container>
  )
}

export default UseCaseSelector

const Container = styled.div`
  min-width: 53%;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
  }

  .selected-info {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    margin-right: 6px;
    flex-shrink: 0;
  }

  .filters {
    display: flex;
    align-items: center;
  }
`
